import { useEffect, useRef } from 'react';
import { useBlocker as useRouterBlocker } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useNotifilcationDialog } from '@src/app/notifications/dialog/hooks';

type UseBlockerParams = {
  when: boolean;
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  skipPathPrefixes?: string[];
};

const DEFAULT_SKIP_PATH_PREFIXES = ['/auth/login'];

function useBlocker({
  when,
  title,
  message,
  confirmLabel,
  cancelLabel,
  skipPathPrefixes = DEFAULT_SKIP_PATH_PREFIXES,
}: UseBlockerParams) {
  const { t } = useTranslation();
  const dialog = useNotifilcationDialog();
  const resolvedTitle = title ?? t('blocker.title', 'Leave this page?');
  const resolvedMessage = message ?? t(
    'blocker.text',
    'You have unsaved changes. Do you want to leave without saving?',
  );
  const resolvedConfirmLabel = confirmLabel ?? t('blocker.confirm_button', 'Confirm');
  const resolvedCancelLabel = cancelLabel ?? t('blocker.cancel_button', 'Cancel');
  const blocker = useRouterBlocker({
    shouldBlockFn: ({ next }) => {
      if (!when) {
        return false;
      }

      return !skipPathPrefixes.some((prefix) => next.pathname.startsWith(prefix));
    },
    withResolver: true,
    enableBeforeUnload: when,
  });
  const isConfirmOpen = useRef(false);

  useEffect(() => {
    if (blocker.status !== 'blocked' || isConfirmOpen.current) {
      return;
    }

    isConfirmOpen.current = true;
    void (async () => {
      const { action } = await dialog.open({
        title: resolvedTitle,
        children: resolvedMessage,
        slotProps: {
          confirmButton: { label: resolvedConfirmLabel },
          cancelButton: { label: resolvedCancelLabel },
        },
      });

      isConfirmOpen.current = false;
      if (action === dialog.NOTIFICATION_DIALOG_ACTIONS.CONFIRM) {
        blocker.proceed?.();
        return;
      }
      blocker.reset?.();
    })();
  }, [blocker, dialog, resolvedCancelLabel, resolvedConfirmLabel, resolvedMessage, resolvedTitle]);
}

export default useBlocker;
